import {
  CurrentStep,
  HtmlFinalStep,
  HtmlStepperFormData,
  HtmlStepperStep,
} from '@superfunnel/types';
import { SetStateAction, useCallback } from 'react';

// Helper to generate unique IDs
const generateId = () => Math.random().toString(36).substring(2, 10);

export interface UseHtmlStepperEditorActionsReturn {
  // Step updates
  updateStep: (stepId: string, updates: Partial<HtmlStepperStep>) => void;
  updateFinalStep: (stepId: string, updates: Partial<HtmlFinalStep>) => void;
  reorderSteps: (steps: HtmlStepperStep[]) => void;
  reorderFinalSteps: (finalSteps: HtmlFinalStep[]) => void;

  // Wrapper actions
  updateWrapper: (wrapper: string) => void;
  addWrapper: () => void;
  deleteWrapper: () => void;

  // Step CRUD
  addStep: () => void;
  addFinalStep: (type: 'redirect' | 'html') => void;
  deleteStep: (stepId: string) => void;
  deleteFinalStep: (stepId: string) => void;
  duplicateStep: (stepId: string) => void;
  duplicateFinalStep: (stepId: string) => void;
}

export interface UseHtmlStepperEditorActionsProps {
  formData: HtmlStepperFormData | null;
  updateFormData: (data: SetStateAction<HtmlStepperFormData | null>) => void;
  currentStep: CurrentStep;
  setCurrentStep: (step: CurrentStep) => void;
  goToStep: (step: CurrentStep) => void;
  /** Called after local form data changes to trigger a preview refresh */
  onDataChanged: () => void;
}

export function useHtmlStepperEditorActions({
  updateFormData,
  currentStep,
  setCurrentStep,
  goToStep,
  onDataChanged,
}: UseHtmlStepperEditorActionsProps): UseHtmlStepperEditorActionsReturn {
  // Helper to navigate to a step (updates both React state and iframe)
  const navigateToStep = useCallback(
    (step: CurrentStep) => {
      setCurrentStep(step);
      goToStep(step);
    },
    [setCurrentStep, goToStep],
  );
  // Update a step
  const updateStep = useCallback(
    (stepId: string, updates: Partial<HtmlStepperStep>) => {
      updateFormData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          steps: prev.steps.map((s) =>
            s.id === stepId ? { ...s, ...updates } : s,
          ),
        };
      });
    },
    [updateFormData],
  );

  // Update a final step
  const updateFinalStep = useCallback(
    (stepId: string, updates: Partial<HtmlFinalStep>) => {
      updateFormData((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          finalSteps: prev.finalSteps.map((s) =>
            s.id === stepId ? ({ ...s, ...updates } as HtmlFinalStep) : s,
          ),
        };
      });
    },
    [updateFormData],
  );

  // Reorder steps
  const reorderSteps = useCallback(
    (steps: HtmlStepperStep[]) => {
      updateFormData((prev) => {
        if (!prev) return prev;
        return { ...prev, steps };
      });
    },
    [updateFormData],
  );

  // Reorder final steps
  const reorderFinalSteps = useCallback(
    (finalSteps: HtmlFinalStep[]) => {
      updateFormData((prev) => {
        if (!prev) return prev;
        return { ...prev, finalSteps };
      });
    },
    [updateFormData],
  );

  // Add a new step
  const addStep = useCallback(() => {
    const newStep: HtmlStepperStep = {
      id: generateId(),
      html: '<div data-sf-step-id="new-step">\n  <!-- Your step content here -->\n</div>',
    };
    updateFormData((prev) => {
      if (!prev) return prev;
      return { ...prev, steps: [...prev.steps, newStep] };
    });
    setCurrentStep({ type: 'step', id: newStep.id });
    onDataChanged();
  }, [updateFormData, setCurrentStep, onDataChanged]);

  // Add a new final step
  const addFinalStep = useCallback(
    (type: 'redirect' | 'html') => {
      const newStep: HtmlFinalStep =
        type === 'redirect'
          ? { id: generateId(), stepType: 'redirect', url: '' }
          : {
              id: generateId(),
              stepType: 'html',
              html: '<div>\n  <!-- Thank you content -->\n</div>',
            };
      updateFormData((prev) => {
        if (!prev) return prev;
        return { ...prev, finalSteps: [...prev.finalSteps, newStep] };
      });
      setCurrentStep({ type: 'finalStep', id: newStep.id });
      onDataChanged();
    },
    [updateFormData, setCurrentStep, onDataChanged],
  );

  // Delete a step
  const deleteStep = useCallback(
    (stepId: string) => {
      updateFormData((prev) => {
        if (!prev) return prev;

        const stepIndex = prev.steps.findIndex((s) => s.id === stepId);
        let newSteps = prev.steps.filter((s) => s.id !== stepId);

        // Clean up defaultNextStep references to the deleted step
        newSteps = newSteps.map((s) =>
          s.defaultNextStep === stepId
            ? { ...s, defaultNextStep: undefined }
            : s,
        );

        // If deleting the current step, select the closest neighboring step
        if (currentStep?.type === 'step' && currentStep.id === stepId) {
          if (newSteps.length > 0) {
            // Prefer the next step (same index), or the last step if deleting the last one
            const newIndex = Math.min(stepIndex, newSteps.length - 1);
            navigateToStep({ type: 'step', id: newSteps[newIndex].id });
          } else if (prev.finalSteps.length > 0) {
            // No regular steps left, select first final step
            navigateToStep({
              type: 'finalStep',
              id: prev.finalSteps[0].id,
            });
          } else {
            navigateToStep(null);
          }
        }

        return { ...prev, steps: newSteps };
      });
    },
    [currentStep, updateFormData, navigateToStep],
  );

  // Delete a final step
  const deleteFinalStep = useCallback(
    (stepId: string) => {
      updateFormData((prev) => {
        if (!prev) return prev;

        const stepIndex = prev.finalSteps.findIndex((s) => s.id === stepId);
        const newFinalSteps = prev.finalSteps.filter((s) => s.id !== stepId);

        // If deleting the current final step, select the closest neighboring step
        if (currentStep?.type === 'finalStep' && currentStep.id === stepId) {
          if (newFinalSteps.length > 0) {
            // Prefer the next final step (same index), or the last one if deleting the last
            const newIndex = Math.min(stepIndex, newFinalSteps.length - 1);
            navigateToStep({
              type: 'finalStep',
              id: newFinalSteps[newIndex].id,
            });
          } else if (prev.steps.length > 0) {
            // No final steps left, select last regular step
            navigateToStep({
              type: 'step',
              id: prev.steps[prev.steps.length - 1].id,
            });
          } else {
            navigateToStep(null);
          }
        }

        // Clean up defaultNextStep references to the deleted final step
        const newSteps = prev.steps.map((s) =>
          s.defaultNextStep === stepId
            ? { ...s, defaultNextStep: undefined }
            : s,
        );

        return { ...prev, steps: newSteps, finalSteps: newFinalSteps };
      });
    },
    [currentStep, updateFormData, navigateToStep],
  );

  // Duplicate a step
  const duplicateStep = useCallback(
    (stepId: string) => {
      const newId = generateId();
      updateFormData((prev) => {
        if (!prev) return prev;
        const stepIndex = prev.steps.findIndex((s) => s.id === stepId);
        if (stepIndex === -1) return prev;
        const stepToDuplicate = prev.steps[stepIndex];
        const newStep: HtmlStepperStep = {
          ...stepToDuplicate,
          id: newId,
          name: stepToDuplicate.name
            ? `${stepToDuplicate.name} (copy)`
            : undefined,
        };
        const newSteps = [...prev.steps];
        newSteps.splice(stepIndex + 1, 0, newStep);
        return { ...prev, steps: newSteps };
      });
      setCurrentStep({ type: 'step', id: newId });
      onDataChanged();
    },
    [updateFormData, setCurrentStep, onDataChanged],
  );

  // Duplicate a final step
  const duplicateFinalStep = useCallback(
    (stepId: string) => {
      const newId = generateId();
      updateFormData((prev) => {
        if (!prev) return prev;
        const stepIndex = prev.finalSteps.findIndex((s) => s.id === stepId);
        if (stepIndex === -1) return prev;
        const stepToDuplicate = prev.finalSteps[stepIndex];
        const newStep: HtmlFinalStep = {
          ...stepToDuplicate,
          id: newId,
          name: stepToDuplicate.name
            ? `${stepToDuplicate.name} (copy)`
            : undefined,
        };
        const newSteps = [...prev.finalSteps];
        newSteps.splice(stepIndex + 1, 0, newStep);
        return { ...prev, finalSteps: newSteps };
      });
      setCurrentStep({ type: 'finalStep', id: newId });
      onDataChanged();
    },
    [updateFormData, setCurrentStep, onDataChanged],
  );

  // Update wrapper HTML
  const updateWrapper = useCallback(
    (wrapper: string) => {
      updateFormData((prev) => {
        if (!prev) return prev;
        return { ...prev, wrapper };
      });
    },
    [updateFormData],
  );

  // Add wrapper
  const addWrapper = useCallback(() => {
    const defaultWrapper = `<div data-sf-steps>
  <!-- Steps will be rendered here -->
</div>`;
    updateFormData((prev) => {
      if (!prev) return prev;
      return { ...prev, wrapper: defaultWrapper };
    });
    setCurrentStep({ type: 'wrapper' });
    onDataChanged();
  }, [updateFormData, setCurrentStep, onDataChanged]);

  // Delete wrapper
  const deleteWrapper = useCallback(() => {
    updateFormData((prev) => {
      if (!prev) return prev;

      // If wrapper was selected, select the closest available step
      if (currentStep?.type === 'wrapper') {
        if (prev.steps.length > 0) {
          navigateToStep({ type: 'step', id: prev.steps[0].id });
        } else if (prev.finalSteps.length > 0) {
          navigateToStep({ type: 'finalStep', id: prev.finalSteps[0].id });
        } else {
          navigateToStep(null);
        }
      }

      const { wrapper: _removed, ...rest } = prev;
      return rest as typeof prev;
    });
  }, [updateFormData, navigateToStep, currentStep]);

  return {
    updateStep,
    updateFinalStep,
    reorderSteps,
    reorderFinalSteps,
    updateWrapper,
    addWrapper,
    deleteWrapper,
    addStep,
    addFinalStep,
    deleteStep,
    deleteFinalStep,
    duplicateStep,
    duplicateFinalStep,
  };
}
